import { CloseIcon } from '@chakra-ui/icons'
import {
  Box,
  BoxProps,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputProps,
  InputRightElement,
  useDisclosure,
} from '@chakra-ui/react'
import { IncrementalSearchResponseData } from 'generated/graphql'
import { FC, useEffect, useRef } from 'react'
import { IncrementalSearchListItem } from '../incremetalSearchListItem'
import { Loading } from '../loading'

export type AutocompleteInputFieldProps = {
  leftIcon?: any
  placeholder?: string
  inputAriaLabel?: string
  // 入力欄の見た目を隣接する要素に合わせたい場合に指定する
  inputProps?: InputProps
  defaultKeyword?: string
  incrementalSearchedData?: IncrementalSearchResponseData[]
  searching?: boolean
  autocompletePopoverWidth?: BoxProps['w']
  // 候補を開いたときに入力欄を空にして、候補を絞り込んでいない状態から始めたい場合に指定する
  clearKeywordOnOpen?: boolean
  onIsAutocompleteOpenChanged?: (isOpen: boolean) => any
  onKeywordChanged?: (keyword: string) => any
  onDataClicked?: (data: IncrementalSearchResponseData) => any
  // 指定したときだけ入力欄にクリアボタンを表示する
  onClearClicked?: () => any
}

export const AutocompleteInputField: FC<AutocompleteInputFieldProps> = ({
  leftIcon,
  placeholder,
  inputAriaLabel,
  inputProps,
  defaultKeyword,
  searching,
  autocompletePopoverWidth,
  clearKeywordOnOpen,
  onIsAutocompleteOpenChanged,
  onKeywordChanged,
  incrementalSearchedData,
  onDataClicked,
  onClearClicked,
}) => {
  const suggestDisclosure = useDisclosure()
  const backgroundInputRef = useRef<HTMLInputElement | null>(null)
  const popOverBoxRef = useRef<HTMLDivElement | null>(null)

  // インクリメンタルサーチのBox外を選択したらBoxを閉じる
  useEffect(() => {
    const onClosePopoverBox = (e: MouseEvent) => {
      if (!popOverBoxRef.current?.contains(e.target as Node)) suggestDisclosure.onClose()
    }
    window.addEventListener('click', onClosePopoverBox)
    return () => window.removeEventListener('click', onClosePopoverBox)
  }, [])

  useEffect(() => {
    onIsAutocompleteOpenChanged?.(suggestDisclosure.isOpen)
  }, [suggestDisclosure.isOpen])

  return (
    <Box position="relative" ref={popOverBoxRef}>
      <InputGroup>
        {leftIcon && (
          <InputLeftElement pointerEvents="none">
            <Icon as={leftIcon} w={6} h={6} color="blue.600" />
          </InputLeftElement>
        )}
        <Input
          {...inputProps}
          ref={backgroundInputRef}
          value={defaultKeyword}
          placeholder={placeholder}
          aria-label={inputAriaLabel}
          readOnly
          onClick={() => {
            suggestDisclosure.onOpen()
          }}
        />
        {onClearClicked && (
          <InputRightElement>
            <IconButton
              aria-label="クリア"
              icon={<CloseIcon boxSize="10px" />}
              boxSize="24px"
              // Buttonのsizeテーマがmin-heightを48pxにしているので、円形にするために打ち消す
              minW="24px"
              minH="24px"
              borderRadius="full"
              color="blue.700"
              variant="ghost"
              onClick={() => onClearClicked()}
            />
          </InputRightElement>
        )}
      </InputGroup>

      {suggestDisclosure.isOpen && (
        <Box
          position="absolute"
          top={'-1px'}
          left={'-1px'}
          right={'-1px'}
          bgColor="white"
          zIndex={10}
          rounded={'lg'}
          boxShadow="md"
          w={autocompletePopoverWidth}
        >
          <Box borderBottomWidth={1} borderColor="gray.50">
            <Input
              {...inputProps}
              defaultValue={clearKeywordOnOpen ? '' : defaultKeyword}
              placeholder={placeholder}
              aria-label={inputAriaLabel}
              border="none"
              _focus={{ border: 'none' }}
              autoFocus
              onChange={e => {
                if (!suggestDisclosure.isOpen) return
                onKeywordChanged?.(e.target.value)
              }}
            />
          </Box>
          {/* 1つのItemがだいたい64pxなので、64 x 10 = 640pxで、スクロールできる感を出すために32px引いている */}
          <Box overflowY="scroll" maxH="608px">
            {incrementalSearchedData?.map((data: IncrementalSearchResponseData) => (
              <IncrementalSearchListItem
                key={data.id}
                data={data}
                onClick={() => {
                  onDataClicked?.(data)
                  suggestDisclosure.onClose()
                }}
              />
            ))}
            {searching && <Loading />}
          </Box>
        </Box>
      )}
    </Box>
  )
}
